import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, AlertTriangle, Scale, RefreshCw, Target, Search, Gavel } from 'lucide-react';

const AppealsComplexCases = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Appeals and Complex Cases
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Clear analysis, a realistic strategy, and disciplined execution.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have a refusal, status complications, or a difficult history, we analyse the decision, reconstruct your immigration timeline, and set out credible options. Where a lawyer is required by law, we coordinate with licensed local counsel and remain your day to day case manager. We regularly handle the United Kingdom, United States, Canada, Germany and wider EU, Australia, and the UAE. Typical response time is 24 to 72 hours. London covers EMEA. New York runs an APAC desk for evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who this is for Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Who this is for
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Refusal Recipients</h3>
              <p className="text-gray-600 text-sm">Applicants who received a refusal or intent to refuse letter</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Complex Histories</h3>
              <p className="text-gray-600 text-sm">Individuals with overstays, prior breaches, or complex travel histories</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Document Concerns</h3>
              <p className="text-gray-600 text-sm">Cases with alleged misrepresentation or document authenticity concerns</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Criminal History</h3>
              <p className="text-gray-600 text-sm">People with criminal cautions or convictions where disclosure is required</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Family Issues</h3>
              <p className="text-gray-600 text-sm">Families with dependency, custody, or consent issues</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Self-Filed Cases</h3>
              <p className="text-gray-600 text-sm">Applicants who filed themselves and now need structured recovery</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Outcomes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Outcomes you can expect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Decision Analysis</h3>
              <p className="text-gray-600 text-sm">Written analysis explaining what the officer relied on and which rules apply</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Strategy Paper</h3>
              <p className="text-gray-600 text-sm">Options comparison with timelines and likelihood of success</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Evidence Plan</h3>
              <p className="text-gray-600 text-sm">Disciplined evidence plan that fixes gaps and avoids contradictions</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Quality Filing</h3>
              <p className="text-gray-600 text-sm">Quality checked filing for refile, review, or appeal with counsel where required</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Status Protection</h3>
              <p className="text-gray-600 text-sm">Plan to protect your status or travel while the matter is pending</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Common Refusal Reasons Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Common refusal reasons we address
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Credibility Concerns</h3>
              <p className="text-gray-600 text-sm">Unclear purpose, weak ties, or conflicting statements</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Financial Evidence</h3>
              <p className="text-gray-600 text-sm">Does not meet format, ownership, or maintenance period rules</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Relationship Evidence</h3>
              <p className="text-gray-600 text-sm">Does not prove durability or cohabitation</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Employment Issues</h3>
              <p className="text-gray-600 text-sm">Salary thresholds not met or wrong occupation code selection</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Study Credibility</h3>
              <p className="text-gray-600 text-sm">GTE concerns that do not match academic and career history</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Source of Funds</h3>
              <p className="text-gray-600 text-sm">Source of funds or wealth not fully evidenced</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Non-Disclosure</h3>
              <p className="text-gray-600 text-sm">Prior refusals, overstays, or criminal history not disclosed</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Technical Errors</h3>
              <p className="text-gray-600 text-sm">Missing pages, name mismatches, or date conflicts</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Strategic Options Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Strategic options we consider
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <RefreshCw className="w-6 h-6 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Refile</h3>
              <p className="text-gray-600 text-sm">Refile with stronger evidence if the route is viable and there is no bar to reapplying</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Administrative Review</h3>
              <p className="text-gray-600 text-sm">Challenge an error of law or process on the papers where the system allows</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Gavel className="w-6 h-6 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Appeal</h3>
              <p className="text-gray-600 text-sm">Appeal to the appropriate body with licensed counsel where required</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Status Preservation</h3>
              <p className="text-gray-600 text-sm">Extensions, bridges, or departure and reapply where permitted</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Route Switch</h3>
              <p className="text-gray-600 text-sm">Switch to a category that better matches your profile and evidence</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Withdraw and Rebuild</h3>
              <p className="text-gray-600 text-sm">Where immediate pursuit would damage long term prospects</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            We explain deadlines and eligibility for each option during strategy because they vary by country.
          </p>
        </div>
      </motion.section>

      {/* What you get from us Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What you get from us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Decision and History Analysis</h3>
              <p className="text-gray-600">Plain language summary of the refusal and timeline of your immigration history with day counts</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Evidence Rebuild</h3>
              <p className="text-gray-600">Gap list for documents, templates for affidavits, and expert reports where they add credibility</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Filing and Follow Through</h3>
              <p className="text-gray-600">Refile pack or review request drafted, coordination with counsel, and hearing preparation</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-3">Risk and Communication</h3>
              <p className="text-gray-600">Honest assessment of prospects and clear written instructions for what to avoid while pending</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* End-to-end process Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            End-to-end process
          </h2>
          <div className="grid md:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                1
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Triage and Document Intake</h3>
              <p className="text-gray-600 text-sm">Request refusal letter, original application, and correspondence. Check deadlines first</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                2
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Strategy Call and Written Plan</h3>
              <p className="text-gray-600 text-sm">Confirm best option, outline evidence tasks, assign responsibilities, and publish timeline</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                3
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Evidence Build and Drafting</h3>
              <p className="text-gray-600 text-sm">Collect new documents, reconcile names and dates, prepare statements, run consistency checks</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                4
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Filing and Appointments</h3>
              <p className="text-gray-600 text-sm">Submit refile or review request, brief counsel for appeal, schedule hearings and prepare for questions</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                5
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-2">Decision and Next Steps</h3>
              <p className="text-gray-600 text-sm">Guide post-decision steps and future renewals, or reassess and advise on next credible path</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Timelines Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Timelines and service levels
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Evidence Pack and Draft</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">1-4</span>
                <span className="text-gray-600 ml-2">weeks</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Depending on gaps and complexity</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-blue-200">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Review/Refile Windows</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">Strict</span>
                <span className="text-gray-600 ml-2">deadlines</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Which we confirm for your case</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Gavel className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Appeal Timelines</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">Varies</span>
                <span className="text-gray-600 ml-2">by country</span>
              </div>
              <p className="text-gray-600 text-sm text-center">And docket availability</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack text-center mb-4">Communication</h3>
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-blue-600">24-72</span>
                <span className="text-gray-600 ml-2">hours</span>
              </div>
              <p className="text-gray-600 text-sm text-center">Response time, faster at deadline points</p>
            </div>
          </div>
        </div>
      </motion.section>


    </div>
  );
};

export default AppealsComplexCases;
